import type { PublicKey } from "@solana/web3.js";
import { aprToApy } from "@stepfinance/common";
import type { LiquidityPool, LiquidityPools } from "@stepfinance/liquidity";

import { LIQUIDITY_PROVIDER_FEE } from "../pools";

export const getTotalLiquidityInUSD = (liquidity: (number | null)[]) =>
  liquidity.reduce<number>((total, l) => (total += l ?? 0), 0);
export const isLiquidityPoolUntrackable = (liquidityInUSD: (number | null)[]) => liquidityInUSD.some((l) => l && l < 0);

export const getLiquidityPoolAPY = (pool: LiquidityPools): number => {
  if (pool.kind === "Saber" || pool.kind === "Saros") {
    const liqA = pool.liquidityInUSD[0] ?? 0;
    if (liqA > 0 && getTotalLiquidityInUSD(pool.liquidityInUSD) > 0 && pool.fees24h && pool.supply) {
      return aprToApy((pool.fees24h / pool.supply) * 100);
    }
    return 0;
  }
  return pool.apy ?? 0;
};

export const PRICE_IMPACT_THRESHOLD = 50;
export const PRICE_IMPACT_BANNER_WARNING_THRESHOLD = 2;
// based on raydiums calculation
// https://github.com/raydium-io/raydium-ui/blob/46c8699b5c3c293aa24215084f27be82511c0b83/src/utils/swap.ts#L84
export const calculatePriceImpact = (
  inputAmount: string | number,
  outputAmount: string | number,
  inputMint: PublicKey,
  outputMint: PublicKey,
  pool: LiquidityPool,
) => {
  const input = typeof inputAmount === "string" ? parseFloat(inputAmount) : inputAmount;
  const output = typeof outputAmount === "string" ? parseFloat(outputAmount) : outputAmount;
  const poolInputLiquidity = pool.mints[0] === inputMint.toBase58() ? pool.liquidity[0] : pool.liquidity[1];
  const poolOutputLiquidity = pool.mints[1] === outputMint.toBase58() ? pool.liquidity[1] : pool.liquidity[0];

  const outBalance = poolOutputLiquidity - output;
  const beforePrice = poolOutputLiquidity / poolInputLiquidity;
  const afterPrice = outBalance / (poolInputLiquidity + (input + LIQUIDITY_PROVIDER_FEE));
  const priceImpact = ((beforePrice - afterPrice) / beforePrice) * 100;
  return priceImpact;
};
